import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ActionType } from 'typesafe-actions';
import { path } from 'ramda';

import { getCertificates, ICertificateItem } from 'KnmPackage/MainModule/common/cryptoHelpers';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ISelectOption, SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { getStorageItem, setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { getKnmModalParams } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionModalParams } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { knmSign, knmSignWithSaveSigner } from 'KnmPackage/MainModule/actions/knmActions';
import { useKnmFormSignHelpers } from 'KnmPackage/MainModule/hooks/useKnmFormSignHelpers';

import styles from './SignModal.module.scss';
import { IRootState } from '../interfaces/IRootState';
import { Hint } from './Hint';

interface IPlanSignModalProps {
  onClose: () => void;
  onSign: (
    certificate: string,
    actGuid?: string,
    prescriptionGuid?: string,
    abortCheckCertificate?: () => void,
    isCancelSign?: boolean,
    onSuccessCheckCertificate?: (
      action?: ActionType<typeof knmSign> | ActionType<typeof knmSignWithSaveSigner>,
    ) => void,
    isSignAfterSuccessCheckCertificateAction?: boolean,
    isNeedSubjectFioCheck?: boolean,
  ) => void;
  onValidation?: (certificate: string) => void;
  renderValidationResultComponent?: (certificate: string) => JSX.Element;
  title: string;
  visible: boolean;
}

interface ISignModalProps {
  modalParams?: {
    [key: string]: unknown;
  };
  modalDecisionParams?: {
    [key: string]: unknown;
  };
}

const FIO_CHECK_RESULT_ERROR_TEXT = 'ФИО в сертификате ЭП не совпадает с ранее указанными ФИО должностного лица';

const SignModalWithoutStore = ({
  onClose,
  onSign,
  onValidation,
  renderValidationResultComponent,
  title,
  visible,
  modalParams,
  modalDecisionParams,
}: IPlanSignModalProps & ISignModalProps) => {
  const [certificates, setCertificates] = useState<ISelectOption<string>[]>([]);
  const [isDisabledButtonSignAfterClick, setIsDisabledButtonSignAfterClick] = useState(false);
  const [certificate, setSelectedCertificate] = useState<ISelectOption<string> | undefined>(undefined);
  const [currentCertificateError, setCurrentCertificateError] = useState<string | undefined>();

  const { getSubjectInfo } = useKnmFormSignHelpers();

  const actGuid = (modalParams?.actGuid || modalDecisionParams?.actGuid) as string | undefined;

  const prescriptionGuid = (modalParams?.prescriptionGuid || modalDecisionParams?.prescriptionGuid) as
    | string
    | undefined;

  const signerSubjectFioForCheck = (modalParams?.signerSubjectFioForCheck ||
    modalDecisionParams?.signerSubjectFioForCheck) as string | undefined;

  const abortCheckCertificate = (modalParams?.abortCheckCertificate || modalDecisionParams?.abortCheckCertificate) as
    | (() => void)
    | undefined;

  const onSuccessCheckCertificate = (modalParams?.onSuccessCheckCertificate ||
    modalDecisionParams?.onSuccessCheckCertificate) as (() => void) | undefined;

  const isSignAfterSuccessCheckCertificateAction = useMemo(() => {
    if (typeof modalParams?.isSignAfterSuccessCheckCertificateAction === 'boolean') {
      return modalParams?.isSignAfterSuccessCheckCertificateAction;
    }

    if (typeof modalDecisionParams?.isSignAfterSuccessCheckCertificateAction === 'boolean') {
      return modalDecisionParams?.isSignAfterSuccessCheckCertificateAction;
    }

    return undefined;
  }, [
    modalParams?.isSignAfterSuccessCheckCertificateAction,
    modalDecisionParams?.isSignAfterSuccessCheckCertificateAction,
  ]);

  const isNeedSubjectFioCheck = useMemo(() => {
    if (typeof modalParams?.isNeedSubjectFioCheck === 'boolean') {
      return modalParams?.isNeedSubjectFioCheck;
    }

    if (typeof modalDecisionParams?.isNeedSubjectFioCheck === 'boolean') {
      return modalDecisionParams?.isNeedSubjectFioCheck;
    }

    return undefined;
  }, [modalParams?.isNeedSubjectFioCheck, modalDecisionParams?.isNeedSubjectFioCheck]);

  const onClickButtonSignBeforeInnerAction = (modalParams?.onClickButtonSignBeforeInnerAction ||
    modalDecisionParams?.onClickButtonSignBeforeInnerAction) as (() => Promise<void>) | undefined;

  /**
   * Фронтовая проверка соответствия ФИО сертификата
   */
  const getCertificateSubjectInfo = useCallback(async (cert: string) => {
    const subjectName = (await path(['SubjectName'], cert)) as string;

    return getSubjectInfo(subjectName);
  }, []);

  useEffect(() => {
    if (isNeedSubjectFioCheck && certificate) {
      getCertificateSubjectInfo(certificate.value).then(si => {
        let normalizedCertificateFio = si.fullname?.toLowerCase().replaceAll(' ', '');

        if (!normalizedCertificateFio && si.givenname && si.surename) {
          normalizedCertificateFio = `${si.givenname} ${si.surename}`.toLowerCase().replaceAll(' ', '');
        }

        if (normalizedCertificateFio && signerSubjectFioForCheck) {
          const normalizedSignerFio = signerSubjectFioForCheck.toLowerCase().replaceAll(' ', '');

          if (normalizedSignerFio !== normalizedCertificateFio) {
            setCurrentCertificateError(FIO_CHECK_RESULT_ERROR_TEXT);
          }
        }
      });
    }

    return () => {
      setCurrentCertificateError(undefined);
    };
  }, [certificate, visible, signerSubjectFioForCheck, isNeedSubjectFioCheck]);

  useEffect(() => {
    if (visible) {
      loadCerts(onClose);
    }
    setIsDisabledButtonSignAfterClick(false);
  }, [onClose, visible]);

  const loadCerts = async (handleClose: () => void) => {
    try {
      await getCertificates().then((result: ICertificateItem[]) => {
        if (result) {
          const certs: ISelectOption<string>[] = result.map(item => {
            return { id: item.serialNumber, label: item.info, value: item.value };
          });

          setCertificates(certs);
          if (certs.length === 1 && certs?.[0]?.id !== certificate?.id) {
            setSelectedCertificate(certs[0]);
          } else if (certs.length > 1) {
            const certSerialNumber = getStorageItem<string | number>(StorageNamesEnum.CERT_SERIAL_NUMBER);

            if (certSerialNumber) {
              const cert = certs.find(item => item.id === certSerialNumber);

              if (cert && cert?.id !== certificate?.id) {
                setSelectedCertificate(cert);
              }
            }
          }
        } else {
          handleClose();
        }
      });
    } catch (err) {
      console.error('error from loadCerts', err);
    }
  };

  const handleSelectChange = useCallback((value: ISelectOption<string>) => {
    setStorageItem(StorageNamesEnum.CERT_SERIAL_NUMBER, value.id);
    setSelectedCertificate(value);
  }, []);

  const signPlan = useCallback(() => {
    if (certificate?.value) {
      if (onValidation) {
        onValidation(certificate.value);
      } else {
        onSign(
          certificate.value,
          actGuid,
          prescriptionGuid,
          abortCheckCertificate,
          undefined,
          onSuccessCheckCertificate,
          isSignAfterSuccessCheckCertificateAction,
          isNeedSubjectFioCheck,
        );
      }
    }
  }, [certificate?.value, onSign, onValidation, actGuid, prescriptionGuid, abortCheckCertificate]);

  const handleSignPlan = useCallback(() => {
    setIsDisabledButtonSignAfterClick(true);
    if (onClickButtonSignBeforeInnerAction) {
      onClickButtonSignBeforeInnerAction().then(signPlan);
    } else {
      signPlan();
    }
  }, [signPlan, onClickButtonSignBeforeInnerAction]);

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalHeader title={title || 'Подписать'} sizeText="medium" weight="bold" color="black" placement="centre" />
      <ModalBody size="content">
        <div className={styles.InputWrapper}>
          <SelectInput
            id="certs"
            name="certs"
            className={styles.SelectInput}
            isClearable={false}
            options={certificates}
            onChange={handleSelectChange}
            value={certificate}
          />
          {currentCertificateError && <Hint variant="alert" text={currentCertificateError} margin="top" />}
        </div>
        {renderValidationResultComponent && renderValidationResultComponent(certificate?.value)}
      </ModalBody>
      <ModalActions>
        <Button
          text="Подписать"
          id="signButton"
          color="primary"
          size="medium"
          onClick={handleSignPlan}
          disabled={!certificate || isDisabledButtonSignAfterClick || !!currentCertificateError}
        />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ISignModalProps => ({
  modalParams: getKnmModalParams(state),
  modalDecisionParams: getDecisionModalParams(state),
});

export const SignModal = connect(mapStateToProps)(SignModalWithoutStore);
